import type { OfferRewardType } from "../../../api/dto/offers.dto.js";
import { formatMinutesShort } from "../../../common/reward_format.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { TransformValue } from "../../../transforms/transform_value.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { setButtonTextOutline } from "../../common_ui_elements/button_text_outline.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { requestChainOfferStep } from "./chain_offer_purchase_service.js";
import { WebGLUIManager } from "../../../webgl_2d/webgl_ui_manager.js";
import { LockInstanceController } from "../../../webgl_2d/webgl_ui/webgl_ui_lock.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { compositeElement } from "../../../common/visual_fx_common_funcs.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import type { GameStatistics } from "../../../storage/statistics_storage.js";
import { areAdsBlockedOffline } from "../../../ads/offline_ads.js";
import { hideOfflineAdTooltip, showAdUnavailableTooltip, showOfflineAdTooltip } from "../../tooltip/offline_ad_tooltip.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { crashCapture } from "../../../crash/crash_reporter.js";

export enum ChainOfferItemType
{
  Price,
  Free,
  Video,
}

export interface ChainOfferRewardFlySource
{
  type: OfferRewardType;
  amount?: number;
  img_id: ImgID;
  center: Vector2;
  size: number;
}

export type ChainOfferRewardsClaimedHandler = ( sources: ChainOfferRewardFlySource[], stats_before: GameStatistics ) => void;

interface ChainOfferRewardMark
{
  icon: UIImage;
  reward: ChainOfferReward;
  img_id: ImgID;
}

export interface ChainOfferReward
{
  type: OfferRewardType;
  amount?: number;
}

export interface ChainOfferItemConfig
{
  offer_id: string;
  step_id: string;
  type: ChainOfferItemType;
  locked: boolean;
  price?: string;
  rewards?: ChainOfferReward[];
}

export class ChainOfferItem extends UIElement
{
  private static readonly CLAIM_ANIM_MS = 180;
  private static readonly STUCK_CHECK_MS = 5000;
  private static readonly REWARD_FLY_MS = 440;
  private static readonly REWARD_APPEAR_MS = 210;
  private static readonly REWARD_FADE_MS = 330;
  private static readonly REWARD_FLY_HEIGHT_MULT = 1;
  private static readonly ITEM_DISAPPEAR_HOLD_MS = 170;
  private static readonly ITEM_POP_SCALE = 1.2;
  private static readonly ITEM_POP_MS = 140;
  private static readonly ITEM_SHRINK_MS = 200;
  private static readonly ITEM_SHRINK_FADE_MS = 120;
  private static readonly APPEAR_POP_MS = 160;
  private static readonly APPEAR_SETTLE_MS = 120;
  private static readonly APPEAR_FADE_MS = 96;
  private static readonly MAX_REWARDS = 3;
  private static readonly REWARD_ICON_GAP_DESIGN = 15;
  private static readonly LOCK_ANIM_DURATION_MS = 1000;
  private static readonly LOCK_GLINT_ANGLE_DEG = 135;
  private static readonly LOCK_GLINT_WIDTH = 0.4;
  private static readonly LOCK_GLINT_INTENSITY = 1.2;
  private static readonly LOCK_WOBBLE_SPEED = 0.05;
  private static readonly LOCK_WOBBLE_MAX_ANGLE = 0.2;

  private static readonly BUTTON_GLINT_DURATION_MS = 1000;
  private static readonly BUTTON_GLINT_ANGLE_DEG = 135;
  private static readonly BUTTON_GLINT_WIDTH = 0.5;
  private static readonly BUTTON_GLINT_INTENSITY = 1.2;

  private readonly config: ChainOfferItemConfig;

  private reward_marks: ChainOfferRewardMark[] = [];
  private on_rewards_claimed?: ChainOfferRewardsClaimedHandler;

  private on_click?: () => void;
  private current_opacity: number = 0;
  
  private button: UIImage | null = null;
  private cached_btn_composite: HTMLCanvasElement | null = null;
  private lock_icon: UIImage | null = null;
  private lock_webgl_ctrl: LockInstanceController | null = null;
  private lock_anim_start: number = 0;

  private on_disappeared?: () => void;
  private on_exit_started?: () => void;
  private on_exit_finished?: () => void;
  private purchase_in_flight = false;
  private claimed = false;
  private shown_ads_offline = false;
  private video_text: UITextElement | null = null;

  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    config: ChainOfferItemConfig
  )
  {
    super( ctx, x, y, width, height, false, false );
    this.config = { ...config };
    this.createElements();
  }

  /** Fired when the card starts leaving, so the chain shift can overlap its exit. */
  public setOnExitStarted( cb: () => void ): void
  {
    this.on_exit_started = cb;
  }

  /** Fired once the card is fully gone, so its owner can detach it. */
  public setOnExitFinished( cb: () => void ): void
  {
    this.on_exit_finished = cb;
  }

  public getStepId(): string
  {
    return this.config.step_id;
  }

  public setOnRewardsClaimed( cb: ChainOfferRewardsClaimedHandler ): void
  {
    this.on_rewards_claimed = cb;
  }

  public unlock(): void
  {
    if ( this.claimed || !this.config.locked )
      return;

    this.config.locked = false;
    this.cached_btn_composite = null;
    this.removeAllChildren();
    this.createElements();
    this.forceMakeShouldBeRendered();

    const now = performance.now();
    const item_pos   = this.getAbsolutePosition();
    const item_rect  = this.getRect();
    const center_pos = new Vector2( item_pos.x + item_rect.getW() / 2, item_pos.y + item_rect.getH() / 2 );
    Vfx2DManager.addSparkRing( center_pos, now, () => {
      this.playButtonGlint();
    } );
    this.playPopAnimation( now );
  }

  public setPrice( price: string ): void
  {
    if ( this.claimed || this.config.price === price ) return;
    this.config.price = price;
    this.removeAllChildren();
    this.createElements();
    this.forceMakeShouldBeRendered();
  }

  public playAppearAnimation( start_time: number ): void
  {
    const settle_start = start_time + ChainOfferItem.APPEAR_POP_MS;

    this.setOpacity( 0 );
    this.setScale( new Vector2( 0, 0 ) );

    this.addTransform( new TransformOpacity( this, true, 0, 1, start_time, ChainOfferItem.APPEAR_FADE_MS, { easing: Easing.EaseOutQuad } ) );
    this.addTransform( new TransformScale( this, true, null, new Vector2( ChainOfferItem.ITEM_POP_SCALE, ChainOfferItem.ITEM_POP_SCALE ), start_time, ChainOfferItem.APPEAR_POP_MS, { easing: Easing.EaseOutQuad } ) );
    this.addTransform( new TransformScale( this, true, null, new Vector2( 1, 1 ), settle_start, ChainOfferItem.APPEAR_SETTLE_MS, { easing: Easing.EaseInQuad } ) );
  }

  public playPopAnimation( start_time: number ): void
  {
    const settle_start = start_time + ChainOfferItem.ITEM_SHRINK_MS;

    this.addTransform( new TransformScale( this, true, null, new Vector2( ChainOfferItem.ITEM_POP_SCALE, ChainOfferItem.ITEM_POP_SCALE ), start_time, ChainOfferItem.ITEM_SHRINK_MS, { easing: Easing.EaseOutQuad } ) );
    this.addTransform( new TransformScale( this, true, null, new Vector2( 1, 1 ), settle_start, ChainOfferItem.ITEM_POP_MS, { easing: Easing.EaseInQuad } ) );
  }

  private createElements(): void
  {
    this.createSubstrate();
    this.createReward();
    this.createButton();
  }

  private createSubstrate(): void
  {
    const substrate_img_id = this.config.locked ? ImgID.UI_ChainOfferSubstrate : ImgID.UI_ChainOfferSubstrateActive;
    const substrate = new UIImage( this.ctx, 0, 0, this.rect.getW(), this.rect.getH(), substrate_img_id );
    this.addChild( substrate );
  }

  private createReward(): void
  {
    this.reward_marks = [];

    const all_rewards = this.config.rewards ?? [];
    const marks = all_rewards
      .slice( 0, ChainOfferItem.MAX_REWARDS )
      .map( reward => ( { reward, img_id: ChainOfferItem.getRewardImgId( reward.type ) } ) )
      .filter( ( entry ): entry is { reward: ChainOfferReward; img_id: ImgID } => entry.img_id !== null );

    if ( !marks.length )
      return;

    const compact_row = marks.length >= 3;
    const reward_icon_w_design = compact_row ? 118 : 155;
    const reward_icon_h_design = compact_row ? 118 : 155;
    const reward_icon_y_design = 51;

    const reward_icon_w = LayoutScaler.widthFromDesign( reward_icon_w_design );
    const reward_icon_h = LayoutScaler.heightFromDesign( reward_icon_h_design );
    const reward_icon_y = LayoutScaler.yFromDesign( reward_icon_y_design );
    const gap = LayoutScaler.widthFromDesign( ChainOfferItem.REWARD_ICON_GAP_DESIGN );

    const single = marks.length === 1;
    const row_w = marks.length * reward_icon_w + ( marks.length - 1 ) * gap;
    const start_x = ( this.rect.getW() - row_w ) / 2;

    marks.forEach( ( { reward, img_id }, index ) =>
    {
      const reward_icon_x = start_x + index * ( reward_icon_w + gap );

      const reward_icon = new UIImage( this.ctx, reward_icon_x, reward_icon_y, reward_icon_w, reward_icon_h, img_id );
      RewardTooltip.attach( reward_icon, reward.type );
      this.addChild( reward_icon );

      const text = reward.type === 'no_ads'
        ? 'NO ADS'
        : reward.amount === undefined
          ? null
          : ChainOfferItem.formatRewardAmount( reward.type, reward.amount ) || null;

      if ( text !== null )
        this.createRewardText( reward_icon, reward_icon_x, reward_icon_w, text, single );

      this.reward_marks.push( { icon: reward_icon, reward, img_id } );
    } );

    const hidden_count = all_rewards.length - marks.length;
    if ( hidden_count > 0 )
      this.createHiddenRewardsLabel( hidden_count );
  }

  private createHiddenRewardsLabel( count: number ): void
  {
    const label = new UITextElement( this.ctx, 0, LayoutScaler.yFromDesign( 205 ), this.rect.getW(), LayoutScaler.heightFromDesign( 38 ), {
      text: `+${count} MORE`,
      font_size: LayoutScaler.textSizeFromDesign( 28 ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_color: ColorScheme.white,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 7 ),
      outline_color: '#003F87',
      auto_wrap: false,
      scale_to_width: true,
    } );
    this.addChild( label, true );
  }

  private createRewardText( reward_icon: UIImage, reward_icon_x: number, reward_icon_w: number, text: string, single: boolean ): void
  {
    const reward_text_y_design = 114;
    const reward_text_h_design = 60;
    const reward_text_font_size_design = 50;
    const reward_text_outline_width_design = 12;
    const reward_text_outline_offset_y_design = 3;
    const reward_text_outline_color = "#003F87";

    const slop = LayoutScaler.widthFromDesign( 20 );
    const reward_text_x = single ? -reward_icon_x : -slop;
    const reward_text_w = single ? this.rect.getW() : reward_icon_w + slop * 2;
    const reward_text_y = LayoutScaler.yFromDesign( reward_text_y_design );
    const reward_text_h = LayoutScaler.heightFromDesign( reward_text_h_design );

    const reward_text = new UITextElement( this.ctx, reward_text_x, reward_text_y, reward_text_w, reward_text_h, {
      text,
      font_size: LayoutScaler.textSizeFromDesign( reward_text_font_size_design ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_color: ColorScheme.white,
      outline_width: LayoutScaler.textBorderSizeFromDesign( reward_text_outline_width_design ),
      outline_color: reward_text_outline_color,
      outline_offset_y: LayoutScaler.yFromDesign( reward_text_outline_offset_y_design ),
      scale_to_width: true,
      auto_wrap: false,
    } );
    reward_icon.addChild( reward_text, true );
  }

  private static getRewardImgId( type: OfferRewardType ): ImgID | null
  {
    switch ( type )
    {
      case "coins": return ImgID.UI_ShopBlockImg;
      case "hearts": return ImgID.IconHeart;
      case "infinite_hearts_minutes": return ImgID.UI_ShopInfiniteHeart;
      case "big_balls": return ImgID.UI_GameStartBigBallBooster;
      case "infinite_balls": return ImgID.UI_GameStartInfiniteBallsBooster;
      case "multi_balls": return ImgID.UI_GameStartMultiballsBooster;
      case "fireworks": return ImgID.UI_GameStartFireworkBooster;
      case "mines": return ImgID.UI_PassiveBoosterBomb;
      case "no_ads": case "no_ads_minutes": return ImgID.UI_RemoveAdsIcon;
      default: return null;
    }
  }

  private static formatRewardAmount( type: OfferRewardType, amount: number ): string
  {
    if ( type === "coins" )
      return `${amount}`;

    if ( type === "no_ads_minutes" )
      return formatMinutesShort( amount );

    if ( type === "infinite_hearts_minutes" )
    {
      const hours = Math.floor( amount / 60 );
      const minutes = amount % 60;
      const h = LocalizationManager.t( "TIME.SHORT_HOURS" );
      const m = LocalizationManager.t( "TIME.SHORT_MINUTES" );
      return hours && minutes ? `${hours}${h} ${minutes}${m}` : hours ? `${hours}${h}` : `${minutes}${m}`;
    }

    return `x${amount}`;
  }

  private createButton(): void
  {
    this.cached_btn_composite = null;
    const button_w_design = 434;
    const button_h_design = 131;
    const button_y_design = 250;

    const button_w = LayoutScaler.widthFromDesign( button_w_design );
    const button_h = LayoutScaler.heightFromDesign( button_h_design );
    const button_x = ( this.rect.getW() - button_w ) / 2;
    const button_y = LayoutScaler.yFromDesign( button_y_design );

    const button_img_id = this.config.type === ChainOfferItemType.Video ? ImgID.UI_ChainOfferVideoIcon : ImgID.UI_ChainOfferGreenBtn;
    this.button = new UIImage( this.ctx, button_x, button_y, button_w, button_h, button_img_id, {}, true );
    this.addChild( this.button );

    this.createButtonContent( this.button );

    if ( this.config.locked )
    {
      this.createLockIcon( this.button );
      this.button.setOnClick( () => this.handleLockedClick() );
      return;
    }

    this.button.setOnClick( () => this.handlePurchaseClick( this.button! ) );
  }

  public playButtonGlint(): void
  {
    if ( !this.button )
      return;

    const now      = performance.now();
    const abs_pos  = this.button.getAbsolutePosition();
    const rect     = this.button.getRect();
    const btn_w    = rect.getW();
    const btn_h    = rect.getH();
    const center   = new Vector2( abs_pos.x + btn_w / 2, abs_pos.y + btn_h / 2 );

    const offset_x = LayoutScaler.widthFromDesign( 40 );
    const offset_y = LayoutScaler.heightFromDesign( 20 );

    if ( !this.cached_btn_composite )
    {
      this.cached_btn_composite = compositeElement( this.button, offset_x, offset_y, this.ctx );
    }
    const btn_composite = this.cached_btn_composite;

    WebGLUIManager.addGlint( {
      position: center,
      size_physical: new Vector2( btn_composite.width, btn_composite.height ),
      canvas: btn_composite,
      glint_angle_deg: ChainOfferItem.BUTTON_GLINT_ANGLE_DEG,
      glint_width: ChainOfferItem.BUTTON_GLINT_WIDTH,
      duration: ChainOfferItem.BUTTON_GLINT_DURATION_MS,
      intensity: ChainOfferItem.BUTTON_GLINT_INTENSITY,
    }, now );
  }

  private syncVideoButtonOfflineState(): void
  {
    if ( !this.button || this.config.type !== ChainOfferItemType.Video || this.config.locked || this.claimed )
      return;

    const ads_offline = areAdsBlockedOffline();
    if ( ads_offline === this.shown_ads_offline ) return;

    this.shown_ads_offline = ads_offline;
    if ( !ads_offline ) hideOfflineAdTooltip();
    this.cached_btn_composite = null;
    this.button.setDimmed( ads_offline );
    if ( this.video_text )
      setButtonTextOutline( [ this.video_text ], !ads_offline, ColorScheme.more_lives_purple_outline );
    this.forceMakeShouldBeRendered();
  }

  private handlePurchaseClick( button: UIImage ): void
  {
    void audioController.playSound( AudioClips.UI_CLICK );

    if ( this.purchase_in_flight )
      return;

    if ( this.config.type === ChainOfferItemType.Video && areAdsBlockedOffline() )
    {
      showOfflineAdTooltip( button );
      return;
    }

    this.purchase_in_flight = true;
    const stats_before = StatisticsStorage.load();

    const step_type = this.config.type === ChainOfferItemType.Free
      ? 'free'
      : this.config.type === ChainOfferItemType.Video
        ? 'rewarded'
        : 'paid';
    void requestChainOfferStep( this.config.offer_id, this.config.step_id, step_type ).then( status =>
    {
      this.purchase_in_flight = false;
      if ( status === 'ad_unavailable' ) showAdUnavailableTooltip( button );
      if ( status === 'claimed' )
      {
        const now        = performance.now();
        const item_pos   = this.getAbsolutePosition();
        const item_rect  = this.getRect();
        const center_pos = new Vector2( item_pos.x + item_rect.getW() / 2, item_pos.y + item_rect.getH() / 2 );

        Vfx2DManager.addConfettiPoint( center_pos, now );
        console.warn( `[Chain offer] claim animation ${this.config.step_id}: button_current=${this.button === button} button_attached=${button.getParentElement() === this} marks=${this.reward_marks.length} hand_off=${!!this.on_rewards_claimed} price=${this.config.price}` );
        this.playPopAnimation( now );
        this.playClaimAnimation( button, stats_before );
        window.setTimeout( () => this.reportStuckClaim( button ), ChainOfferItem.STUCK_CHECK_MS );
      }
    } );
  }

  private reportStuckClaim( button: UIImage ): void
  {
    if ( !this.getParentElement() )
      return;

    crashCapture( new Error( `chain_card_stuck_after_claim ${this.config.step_id}` ), {
      step_id: this.config.step_id,
      offer_id: this.config.offer_id,
      type: this.config.type,
      price: this.config.price,
      claimed: this.claimed,
      button_current: this.button === button,
      button_attached: button.getParentElement() === this,
      reward_marks: this.reward_marks.length,
      hand_off: !!this.on_rewards_claimed,
      transforms: this.activeTransformCount(),
      visibility: document.visibilityState,
    } );
  }

  private playClaimAnimation( button: UIImage, stats_before: GameStatistics ): void
  {
    this.claimed = true;

    const now = performance.now();
    const confirm_start = now + ChainOfferItem.CLAIM_ANIM_MS;
    const item_disappear_start = confirm_start + ChainOfferItem.CLAIM_ANIM_MS + ChainOfferItem.ITEM_DISAPPEAR_HOLD_MS;

    button.setClickable( false );
    this.playButtonDisappear( button, now );
    this.playConfirmMarkAppear( button, confirm_start );
    this.playItemDisappear( now, item_disappear_start );

    if ( !this.reward_marks.length )
      return;

    if ( this.on_rewards_claimed )
      this.playRewardHandOff( now, stats_before );
    else
      this.playRewardFlyUp( now );
  }

  private playRewardHandOff( now: number, stats_before: GameStatistics ): void
  {
    const marks = this.reward_marks;
    this.reward_marks = [];

    for ( const { icon } of marks )
    {
      icon.setScale( new Vector2( 0.6, 0.6 ) );
      icon.addTransform( new TransformScale( icon, true, null, new Vector2( 1, 1 ), now, ChainOfferItem.REWARD_APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
    }

    const hand_off = new TransformValue( 0, 1, now, ChainOfferItem.REWARD_APPEAR_MS, () => {} );
    hand_off.setOnFinished( () =>
    {
      const sources: ChainOfferRewardFlySource[] = marks.map( ( { icon, reward, img_id } ) =>
      {
        const pos = icon.getAbsolutePosition();
        const rect = icon.getRect();
        return {
          type: reward.type,
          ...( reward.amount !== undefined ? { amount: reward.amount } : {} ),
          img_id,
          center: new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 ),
          size: rect.getW(),
        };
      } );

      for ( const { icon } of marks )
        this.removeChild( icon );

      this.on_rewards_claimed?.( sources, stats_before );
    } );
    this.addTransform( hand_off );
  }

  private playItemDisappear( now: number, start_time: number ): void
  {
    if ( this.lock_webgl_ctrl )
    {
      this.lock_webgl_ctrl.stop();
      this.lock_webgl_ctrl = null;
    }
    const shrink_start = start_time + ChainOfferItem.ITEM_POP_MS;
    const fade_start = shrink_start + ChainOfferItem.ITEM_SHRINK_MS - ChainOfferItem.ITEM_SHRINK_FADE_MS;

    // The chain shift has to overlap this exit, so it is kicked off when the card
    // starts leaving rather than when it is gone. TransformValue is used purely as
    // a scheduler here: it runs on the same frame clock as the animation it is
    // timed against, where a setTimeout would drift and would still fire after the
    // screen was closed.
    const exit_trigger = new TransformValue( 0, 1, now, start_time - now, () => {} );
    exit_trigger.setOnFinished( () => this.on_exit_started?.() );
    this.addTransform( exit_trigger );

    const fade_out = new TransformOpacity( this, true, null, 0, fade_start, ChainOfferItem.ITEM_SHRINK_FADE_MS, { easing: Easing.EaseInQuad } );
    fade_out.setOnFinished( () => this.on_exit_finished?.() );

    this.addTransform( new TransformScale( this, true, null, new Vector2( ChainOfferItem.ITEM_POP_SCALE, ChainOfferItem.ITEM_POP_SCALE ), start_time, ChainOfferItem.ITEM_POP_MS, { easing: Easing.EaseOutQuad } ) );
    this.addTransform( new TransformScale( this, true, null, new Vector2( 0, 0 ), shrink_start, ChainOfferItem.ITEM_SHRINK_MS, { easing: Easing.EaseInQuad } ) );
    this.addTransform( fade_out );
  }

  private playButtonDisappear( button: UIImage, now: number ): void
  {
    const scale_out = new TransformScale( button, true, null, new Vector2( 0, 0 ), now, ChainOfferItem.CLAIM_ANIM_MS, { easing: Easing.EaseInQuad } );
    const fade_out = new TransformOpacity( button, true, null, 0, now, ChainOfferItem.CLAIM_ANIM_MS, { easing: Easing.EaseInQuad } );
    fade_out.setOnFinished( () => this.removeChild( button ) );

    button.addTransform( scale_out );
    button.addTransform( fade_out );
  }

  private playConfirmMarkAppear( button: UIImage, start_time: number ): void
  {
    const confirm_mark_w_design = 100;
    const confirm_mark_h_design = 92;

    const confirm_mark_w = LayoutScaler.widthFromDesign( confirm_mark_w_design );
    const confirm_mark_h = LayoutScaler.heightFromDesign( confirm_mark_h_design );
    const button_rect = button.getRect();
    const confirm_mark_x = button.pixelPosImportant().x + ( button_rect.getW() - confirm_mark_w ) / 2;
    const confirm_mark_y = button.pixelPosImportant().y + ( button_rect.getH() - confirm_mark_h ) / 2;

    const confirm_mark = new UIImage( this.ctx, confirm_mark_x, confirm_mark_y, confirm_mark_w, confirm_mark_h, ImgID.UI_DailyTasksProgressConfirm );
    this.addChild( confirm_mark );

    confirm_mark.setOpacity( 0 );
    confirm_mark.setScale( new Vector2( 0.5, 0.5 ) );
    confirm_mark.addTransform( new TransformOpacity( confirm_mark, true, 0, 1, start_time, ChainOfferItem.CLAIM_ANIM_MS, { easing: Easing.EaseOutQuad } ) );
    confirm_mark.addTransform( new TransformScale( confirm_mark, true, null, new Vector2( 1, 1 ), start_time, ChainOfferItem.CLAIM_ANIM_MS, { easing: Easing.EaseOutQuad } ) );
  }

  /** The reward icons themselves fly out, so no copy is left behind in the card. */
  private playRewardFlyUp( now: number ): void
  {
    const fly_start = now + ChainOfferItem.REWARD_APPEAR_MS;
    const fade_out_start = fly_start + ChainOfferItem.REWARD_FLY_MS - ChainOfferItem.REWARD_FADE_MS;

    for ( const { icon: reward_icon } of this.reward_marks )
    {
      // The position lives on the transformable, not on the rect: UIElement
      // always builds its rect at 0,0, so only the size may be read from it.
      const pos = reward_icon.pixelPosImportant();
      const dest = new Vector2( pos.x, pos.y - reward_icon.getRect().getH() * ChainOfferItem.REWARD_FLY_HEIGHT_MULT );

      reward_icon.setScale( new Vector2( 0.6, 0.6 ) );
      reward_icon.addTransform( new TransformScale( reward_icon, true, null, new Vector2( 1, 1 ), now, ChainOfferItem.REWARD_APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
      reward_icon.addTransform( new TransformStraightMove( reward_icon, true, null, dest, fly_start, ChainOfferItem.REWARD_FLY_MS, { easing: Easing.EaseOutQuad } ) );

      const fade_out = new TransformOpacity( reward_icon, true, 1, 0, fade_out_start, ChainOfferItem.REWARD_FADE_MS, { easing: Easing.EaseInQuad } );
      fade_out.setOnFinished( () => this.removeChild( reward_icon ) );
      reward_icon.addTransform( fade_out );
    }

    this.reward_marks = [];
  }

  private createButtonContent( button: UIImage ): void
  {
    switch ( this.config.type )
    {
      case ChainOfferItemType.Free:
        this.createButtonText( button, undefined, 60, "CHAIN_OFFER.BUTTON.FREE" );
        break;
      case ChainOfferItemType.Price:
        this.createButtonText( button, this.config.price ?? "", 50 );
        break;
      case ChainOfferItemType.Video:
        this.createVideoButtonContent( button );
        break;
    }
  }

  private createButtonText( button: UIImage, text: string | undefined, font_size_design: number, localization_key?: string ): void
  {
    const text_shadow_offset_y_design = 6;
    const text_shadow_blur_design = 6;
    const text_width_ratio = 0.8;
    const text_width_ratio_locked = 0.55;

    const button_w = button.getRect().getW();
    const text_w = button_w * ( this.config.locked ? text_width_ratio_locked : text_width_ratio );
    const text_x = ( button_w - text_w ) / 2;

    const text_element = new UITextElement( this.ctx, text_x, 0, text_w, button.getRect().getH(), {
      text,
      localization_key,
      font_size: LayoutScaler.textSizeFromDesign( font_size_design ),
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_color: ColorScheme.white,
      shadow_color: ColorScheme.black_transparent,
      shadow_blur: LayoutScaler.widthFromDesign( text_shadow_blur_design ),
      shadow_offset_y: LayoutScaler.yFromDesign( text_shadow_offset_y_design ),
      scale_to_width: true,
      auto_wrap: false,
    } );
    button.addChild( text_element, true );
  }

  /** The video icon is baked into the button substrate art itself (ImgID.UI_ChainOfferVideoIcon); only the label goes on top. */
  private createVideoButtonContent( button: UIImage ): void
  {
    const text_x_design = 127;
    const text_w_design = 250;
    const text_font_size_design = 40;
    const text_shadow_offset_y_design = 6;
    const text_shadow_blur_design = 6;

    const button_h = button.getRect().getH();
    const text_x = LayoutScaler.xFromDesign( text_x_design );
    const text_w = LayoutScaler.widthFromDesign( text_w_design );

    const text_element = new UITextElement( this.ctx, text_x, 0, text_w, button_h, {
      localization_key: "CHAIN_OFFER.BUTTON.WATCH",
      font_size: LayoutScaler.textSizeFromDesign( text_font_size_design ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_color: ColorScheme.white,
      shadow_color: ColorScheme.black_transparent,
      shadow_blur: LayoutScaler.widthFromDesign( text_shadow_blur_design ),
      shadow_offset_y: LayoutScaler.yFromDesign( text_shadow_offset_y_design ),
      scale_to_width: true,
      auto_wrap: false,
      outline_color: ColorScheme.more_lives_purple_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } );
    button.addChild( text_element, true );
    this.video_text = text_element;
  }

  private createLockIcon( button: UIImage ): void
  {
    const lock_icon_w_design = 91;
    const lock_icon_h_design = 100;
    const lock_icon_x_design = 350;
    const lock_icon_y_design = 8;

    const lock_icon_w = LayoutScaler.widthFromDesign( lock_icon_w_design );
    const lock_icon_h = LayoutScaler.heightFromDesign( lock_icon_h_design );
    const lock_icon_x = LayoutScaler.xFromDesign( lock_icon_x_design );
    const lock_icon_y = LayoutScaler.yFromDesign( lock_icon_y_design );

    this.lock_icon = new UIImage( this.ctx, lock_icon_x, lock_icon_y, lock_icon_w, lock_icon_h, ImgID.UI_ChainOfferLockIcon );
    button.addChild( this.lock_icon, true );
  }

  private getLockAbsoluteCenter(): Vector2
  {
    if ( !this.lock_icon ) return new Vector2( 0, 0 );
    const abs_pos = this.lock_icon.getAbsolutePosition();
    const lock_rect = this.lock_icon.getRect();
    return new Vector2(
      abs_pos.x + lock_rect.getW() / 2,
      abs_pos.y + lock_rect.getH() / 2
    );
  }

  private handleLockedClick(): void
  {
    if ( this.lock_webgl_ctrl || !this.lock_icon )
      return;

    void audioController.playSound( AudioClips.UI_CLICK );

    this.lock_anim_start = performance.now();
    this.lock_icon.setOpacityImmediate( 0 );
    this.forceMakeShouldBeRendered();

    const glint_duration = ChainOfferItem.LOCK_ANIM_DURATION_MS;
    const center_pos     = this.getLockAbsoluteCenter();

    this.lock_webgl_ctrl = WebGLUIManager.addLock( {
      position: center_pos,
      size_physical: new Vector2( this.lock_icon.getRect().getW(), this.lock_icon.getRect().getH() ),
      glint_angle_deg: ChainOfferItem.LOCK_GLINT_ANGLE_DEG,
      glint_width: ChainOfferItem.LOCK_GLINT_WIDTH,
      duration: glint_duration,
      intensity: ChainOfferItem.LOCK_GLINT_INTENSITY,
      on_finish: () =>
      {
        this.lock_webgl_ctrl = null;
        if ( this.lock_icon )
        {
          this.lock_icon.setOpacityImmediate( 1 );
          this.forceMakeShouldBeRendered();
        }
      }
    }, this.lock_anim_start );
  }

  public override updateBasic( now: number ): void
  {
    super.updateBasic( now );
    this.syncVideoButtonOfflineState();

    if ( this.lock_webgl_ctrl && this.lock_icon )
    {
      const elapsed  = now - this.lock_anim_start;
      const duration = ChainOfferItem.LOCK_ANIM_DURATION_MS;
      
      if ( elapsed >= duration )
      {
        this.lock_webgl_ctrl.stop();
        this.lock_webgl_ctrl = null;
        this.lock_icon.setOpacityImmediate( 1 );
        this.forceMakeShouldBeRendered();
      }
      else
      {
        // Enforce that the 2D lock remains 100% hidden during the entire animation
        if ( this.lock_icon.getOpacity() > 0.001 )
        {
          this.lock_icon.setOpacityImmediate( 0 );
          this.forceMakeShouldBeRendered();
        }

        const wobble_speed = ChainOfferItem.LOCK_WOBBLE_SPEED;
        const max_angle    = ChainOfferItem.LOCK_WOBBLE_MAX_ANGLE;
        
        // Envelope: 0 -> 1 -> 0 to make the wobble start and end smoothly
        const envelope = Math.sin( ( elapsed / duration ) * Math.PI );
        const angle    = Math.sin( elapsed * wobble_speed ) * max_angle * envelope;
        
        const lock_rect  = this.lock_icon.getRect();
        const center_pos = this.getLockAbsoluteCenter();
        
        const opacity = this.getActualOpacity();
        this.lock_webgl_ctrl.setTransformAndWobble( center_pos, angle, new Vector2(lock_rect.getW(), lock_rect.getH()), opacity );
      }
    }
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "ChainOfferItem";
  }
}
